import {useDispatch, useSelector} from 'react-redux';
import {useSaveRunfile} from '../../../../../hooks/useSaveRunfile';
import {DEBATABLE_ANSWER_COLOR} from '../../../../../incase-domain/common/color';
import {IQuestionAnswerType} from '../../../../../incase-domain/elements/base/questionElementBase';
import {IElementQuestionYesNo} from '../../../../../incase-domain/elements/question/questionYesNo';
import {IGameConfig} from '../../../../../incase-domain/proptypes/gameConfig';
import {YesNoElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {getNewPoints} from '../../../../../service/gamification.service';
import {fetchQuestionReportAction} from '../../../../../store/actions/analytics.actions';
import {handleElementsClickedAction} from '../../../../../store/actions/chalkBoardElements.actions';
import {emitToastAction} from '../../../../../store/actions/questionToast.actions';
import {gameConfigSelector} from '../../../../../store/selectors/presentation.selectors';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';

interface UseYesNoQuestionActionsReturn {
    selectAnswer: (element: IElementQuestionYesNo, elementInteractions: YesNoElementInteractions, answer?: number) => void;
    confirmAnswer: (element: IElementQuestionYesNo, elementInteractions: YesNoElementInteractions) => void;
}

function useYesNoQuestionActions(): UseYesNoQuestionActionsReturn {
    const dispatch = useDispatch();
    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);
    const saveRunfile = useSaveRunfile();

    const selectAnswer = (element: IElementQuestionYesNo, elementInteractions: YesNoElementInteractions, answer?: number): void => {
        if (elementInteractions.answered) {
            return;
        }

        saveRunfile(
            {
                answer,
                answered: false,
            },
            element.id,
            element.slideId,
            points || 0
        );
    };

    const confirmAnswer = (element: IElementQuestionYesNo, elementInteractions: YesNoElementInteractions): void => {
        if (elementInteractions.answered || elementInteractions.answer === undefined) {
            return;
        }

        let answeredCorrectly: IQuestionAnswerType = 'incorrect';
        let answerBoxColor;

        if (!element.isDebatable) {
            if (element.correct === elementInteractions.answer) answeredCorrectly = 'correct';
            answerBoxColor = answeredCorrectly ? '#4fffc3' : '#ffa1ae';
        }
        if (element.isDebatable) {
            answeredCorrectly = 'debatable';
            answerBoxColor = DEBATABLE_ANSWER_COLOR;
        }

        const newPoints = getNewPoints(points || 0, answeredCorrectly, gameConfig || ({} as IGameConfig), element.gameConfig);

        saveRunfile(
            {
                answered: true,
                answer: elementInteractions.answer,
                answerBoxColor,
                answeredCorrectly,
            },
            element.id,
            element.slideId,
            newPoints
        );

        dispatch(handleElementsClickedAction([element.id], !!element.clearAfterClicked));
        dispatch(emitToastAction({type: answeredCorrectly, questionId: element.id}));
        dispatch(fetchQuestionReportAction(element.id));
    };

    return {
        selectAnswer,
        confirmAnswer,
    };
}

export default useYesNoQuestionActions;
